/**
 * @file Instance Axios partagée et table centralisée des endpoints de l'API.
 * @module utils/axios
 *
 * Configure une instance Axios unique. Le renouvellement de session n'est pas géré ici :
 * le serveur renouvelle de façon transparente sur n'importe quelle route authentifiée, et
 * le client n'a donc aucun token à rafraîchir. L'intercepteur de réponse se contente de
 * trancher entre deux issues : le serveur a déclaré la session finie, ou l'erreur remonte
 * à l'appelant qui saura quoi en faire.
 *
 * L'objet `endpoints` centralise tous les chemins d'API en trois familles :
 * - `app` : endpoints métier par établissement (scoped par companyId)
 * - `public` : endpoints sans authentification (boutique, réservations publiques)
 * - `admin` : endpoints back-office Brydge
 *
 * Les endpoints V1 du widget embarquable (auth par clé API) vivent à part dans
 * `axiosWidget.js` (`widgetEndpoints`), avec leur propre instance Axios.
 */
import axios from 'axios';
import {HOST_API} from 'apps/dashboard/ConfigGlobal';
import {isSessionClosed} from './authFailure';
import {terminateSession} from './sessionTermination';

const axiosInstance = axios.create({
  baseURL: HOST_API,
  withCredentials: true, // Nécessaire pour envoyer/recevoir les cookies
});


/**
 * Construit les options Axios pour une requête authentifiée par API key externe.
 * Renvoie un objet vide si la clé est absente ou ne respecte pas le format attendu.
 *
 * @param {string} apiKey - Clé API publique (doit commencer par `pub_`)
 * @return {Object} Options Axios avec header `x-api-key`, ou objet vide si clé invalide
 */
export const withApiKey = (apiKey) => {
  if (!apiKey) {
    return {};
  }

  if (apiKey && !apiKey.startsWith('pub_')) {
    return {};
  }

  return {
    headers: {
      'x-api-key': apiKey,
    },
  };
};

/**
 * Table des endpoints de l'API, organisée par famille et domaine métier.
 * Les fonctions acceptent un `companyId` pour scoper les routes par établissement.
 */
export const endpoints = {
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    register: '/auth/register',
    verifyEmail: (token) => `/auth/verify-email/${token}`,
    resendVerificationEmail: '/auth/resend-verification-email',
    user: '/auth/profile',
    refreshToken: '/auth/refresh-token',
    verifyToken: '/auth/verify-token',
    requestResetPassword: '/auth/request-reset-password',
    resetPassword: '/auth/reset-password',
    verifyResetToken: (token) => `/auth/verify-reset-token?token=${token}`,
    // Company Switcher endpoints
    companies: {
      accessible: '/auth/companies/accessible',
      current: '/auth/companies/current',
      switch: (companyId) => `/auth/companies/switch/${companyId}`,
      validate: (companyId) => `/auth/companies/validate/${companyId}`,
      history: '/auth/companies/history',
    },
  },
  app: {
    user: {
      create: (companyId) => `/app/${companyId}/user/create-user`,
      update: (companyId, userId) => `/app/${companyId}/user/update-user/${userId}`,
      updateAsAdmin: (companyId, userId) => `/app/${companyId}/user/update-user-admin/${userId}`,
      getUsersByAccount: (companyId, accountId) => `/app/${companyId}/user/${accountId}/all`,
      changePassword: (companyId) => `/app/${companyId}/user/change-password`,
      toggleActive: (companyId, userId) => `/app/${companyId}/user/toggle-active/${userId}`,
    },
    company: {
      details: (companyId) => `/app/${companyId}/company/details`,
      update: (companyId) => `/app/${companyId}/company/update`,
      updateGiftVoucherTemplate: (companyId) => `/app/${companyId}/company/gift-voucher-template`,
      stripeConnect: (companyId) => `/app/${companyId}/stripe/connect/standard`,
      // Partage multi-établissements
      shareable: (companyId) => `/app/${companyId}/company/shareable`,
    },
    permissions: {
      me: (companyId) => `/app/${companyId}/permissions/me`,
      features: (companyId) => `/app/${companyId}/permissions/features`,
      check: (companyId, featureKey) => `/app/${companyId}/permissions/check/${featureKey}`,
      get: (companyId) => `/app/${companyId}/permissions/config`,
      roles: (companyId) => `/app/${companyId}/permissions/roles`,
      update: (companyId) => `/app/${companyId}/permissions/config`,
      updateRole: (companyId, roleKey) => `/app/${companyId}/permissions/role/${roleKey}`,
      addToRole: (companyId, roleKey, featureKey) => `/app/${companyId}/permissions/role/${roleKey}/${featureKey}`,
      removeFromRole: (companyId, roleKey, featureKey) => `/app/${companyId}/permissions/role/${roleKey}/${featureKey}`,
      toggle: (companyId, roleKey, featureKey) => `/app/${companyId}/permissions/toggle/${roleKey}/${featureKey}`,
    },
    customer: {
      all: (companyId) => `/app/${companyId}/customer/all`,
      details: (companyId, customerId) => `/app/${companyId}/customer/details/${customerId}`,
      linked: (companyId, customerId) => `/app/${companyId}/customer/linked/${customerId}`,
      create: (companyId) => `/app/${companyId}/customer/create`,
      update: (companyId, customerId) => `/app/${companyId}/customer/update/${customerId}`,
      search: (companyId) => `/app/${companyId}/customer/search`,
      export: (companyId) => `/app/${companyId}/customer/export`,
      config: (companyId) => `/app/${companyId}/customer/config`,
      duplicates: (companyId) => `/app/${companyId}/customer/duplicates`,
      mergePreview: (companyId) => `/app/${companyId}/customer/merge-preview`,
      merge: (companyId) => `/app/${companyId}/customer/merge`,
    },
    employees: {
      employee: {
        all: (companyId) => `/app/${companyId}/employee/all`,
        details: (companyId, employeeId) => `/app/${companyId}/employee/details/${employeeId}`,
        create: (companyId) => `/app/${companyId}/employee/create`,
        update: (companyId, employeeId) => `/app/${companyId}/employee/update/${employeeId}`,
        delete: (companyId, employeeId) => `/app/${companyId}/employee/delete/${employeeId}`,
      },
      skill: {
        all: (companyId) => `/app/${companyId}/skill/all`,
        details: (companyId, skillId) => `/app/${companyId}/skill/details/${skillId}`,
        create: (companyId) => `/app/${companyId}/skill/create`,
        update: (companyId, skillId) => `/app/${companyId}/skill/update/${skillId}`,
        delete: (companyId, skillId) => `/app/${companyId}/skill/delete/${skillId}`,
      },
    },
    wellness: {
      config: {
        get: (companyId) => `/app/${companyId}/wellness/wellness-config/get`,
        create: (companyId) => `/app/${companyId}/wellness/wellness-config/create`,
        update: (companyId) => `/app/${companyId}/wellness/wellness-config/update`,
        updateCalendarBanner: (companyId) => `/app/${companyId}/wellness/wellness-config/calendar-banner`,
        validateSetup: (companyId) => `/app/${companyId}/wellness/wellness-config/validate-setup`,
      },
      scheduleExceptions: {
        list: (companyId) => `/app/${companyId}/wellness/schedule-exceptions/all`,
        create: (companyId) => `/app/${companyId}/wellness/schedule-exceptions/create`,
        closure: (companyId) => `/app/${companyId}/wellness/schedule-exceptions/closure`,
        update: (companyId, exceptionId) => `/app/${companyId}/wellness/schedule-exceptions/${exceptionId}`,
        delete: (companyId, exceptionId) => `/app/${companyId}/wellness/schedule-exceptions/${exceptionId}`,
        hours: (companyId) => `/app/${companyId}/wellness/schedule-exceptions/hours`,
        limits: (companyId) => `/app/${companyId}/wellness/schedule-exceptions/limits`,
        range: (companyId) => `/app/${companyId}/wellness/schedule-exceptions/range`,
        recurring: (companyId) => `/app/${companyId}/wellness/schedule-exceptions/recurring`,
        checkBookings: (companyId) => `/app/${companyId}/wellness/schedule-exceptions/check-bookings`,
      },
      breaks: {
        getAllDefault: (companyId) => `/app/${companyId}/wellness/breaks/default`,
        getDefault: (companyId, dayOfWeek) => `/app/${companyId}/wellness/breaks/default/${dayOfWeek}`,
        updateDefault: (companyId, dayOfWeek) => `/app/${companyId}/wellness/breaks/default/${dayOfWeek}`,
        getEffective: (companyId, date) => `/app/${companyId}/wellness/breaks/effective/${date}`,
        getEffectiveByResource: (companyId, date, resourceId) => `/app/${companyId}/wellness/breaks/effective/${date}/${resourceId}`,
        customize: (companyId) => `/app/${companyId}/wellness/breaks/customize`,
      },
      availabilityConfig: (companyId) => `/app/${companyId}/wellness/availability-config/get`,
      spaceGroup: {
        all: (companyId) => `/app/${companyId}/wellness/space-groups/all`,
        details: (companyId, spaceGroupId) => `/app/${companyId}/wellness/space-groups/details/${spaceGroupId}`,
        create: (companyId) => `/app/${companyId}/wellness/space-groups/create`,
        update: (companyId, spaceGroupId) => `/app/${companyId}/wellness/space-groups/update/${spaceGroupId}`,
        delete: (companyId, spaceGroupId) => `/app/${companyId}/wellness/space-groups/delete/${spaceGroupId}`,
        reorder: (companyId) => `/app/${companyId}/wellness/space-groups/reorder`,
      },
      space: {
        all: (companyId) => `/app/${companyId}/wellness/space/all`,
        details: (companyId, spaceId) => `/app/${companyId}/wellness/space/details/${spaceId}`,
        create: (companyId) => `/app/${companyId}/wellness/space/create`,
        update: (companyId, spaceId) => `/app/${companyId}/wellness/space/update/${spaceId}`,
        delete: (companyId, spaceId) => `/app/${companyId}/wellness/space/delete/${spaceId}`,
        reorder: (companyId) => `/app/${companyId}/wellness/space/reorder`,
      },
      serviceCategory: {
        all: (companyId) => `/app/${companyId}/wellness/service-category/all`,
        details: (companyId, categoryId) => `/app/${companyId}/wellness/service-category/details/${categoryId}`,
        create: (companyId) => `/app/${companyId}/wellness/service-category/create`,
        update: (companyId, categoryId) => `/app/${companyId}/wellness/service-category/update/${categoryId}`,
        delete: (companyId, categoryId) => `/app/${companyId}/wellness/service-category/delete/${categoryId}`,
      },
      service: {
        all: (companyId) => `/app/${companyId}/wellness/service/all`,
        details: (companyId, serviceId) => `/app/${companyId}/wellness/service/details/${serviceId}`,
        create: (companyId) => `/app/${companyId}/wellness/service/create`,
        update: (companyId, serviceId) => `/app/${companyId}/wellness/service/update/${serviceId}`,
        delete: (companyId, serviceId) => `/app/${companyId}/wellness/service/delete/${serviceId}`,
        calculatePrice: (companyId, serviceId) => `/app/${companyId}/wellness/service/${serviceId}/calculate-price`,
      },

      extraCategory: {
        all: (companyId) => `/app/${companyId}/wellness/extra-category/all`,
        details: (companyId, categoryId) => `/app/${companyId}/wellness/extra-category/details/${categoryId}`,
        create: (companyId) => `/app/${companyId}/wellness/extra-category/create`,
        update: (companyId, categoryId) => `/app/${companyId}/wellness/extra-category/update/${categoryId}`,
        delete: (companyId, categoryId) => `/app/${companyId}/wellness/extra-category/delete/${categoryId}`,
      },
      extra: {
        all: (companyId) => `/app/${companyId}/wellness/extra/all`,
        details: (companyId, extraId) => `/app/${companyId}/wellness/extra/details/${extraId}`,
        create: (companyId) => `/app/${companyId}/wellness/extra/create`,
        update: (companyId, extraId) => `/app/${companyId}/wellness/extra/update/${extraId}`,
        delete: (companyId, extraId) => `/app/${companyId}/wellness/extra/delete/${extraId}`,
      },
      productCategory: {
        all: (companyId) => `/app/${companyId}/wellness/product-category/all`,
        details: (companyId, categoryId) => `/app/${companyId}/wellness/product-category/details/${categoryId}`,
        create: (companyId) => `/app/${companyId}/wellness/product-category/create`,
        update: (companyId, categoryId) => `/app/${companyId}/wellness/product-category/update/${categoryId}`,
        delete: (companyId, categoryId) => `/app/${companyId}/wellness/product-category/delete/${categoryId}`,
      },
      product: {
        all: (companyId) => `/app/${companyId}/wellness/product/all`,
        details: (companyId, productId) => `/app/${companyId}/wellness/product/details/${productId}`,
        create: (companyId) => `/app/${companyId}/wellness/product/create`,
        update: (companyId, productId) => `/app/${companyId}/wellness/product/update/${productId}`,
        delete: (companyId, productId) => `/app/${companyId}/wellness/product/delete/${productId}`,
      },
      booking: {
        create: (companyId) => `/app/${companyId}/wellness/booking/create`,
        update: (companyId, bookingId) => `/app/${companyId}/wellness/booking/update/${bookingId}`,
        updateParticipants: (companyId, bookingId) => `/app/${companyId}/wellness/booking/participants/${bookingId}`,
        addSlot: (companyId, bookingId) => `/app/${companyId}/wellness/booking/${bookingId}/add-slot`,
        addGroup: (companyId, bookingId) => `/app/${companyId}/wellness/booking/${bookingId}/slots/group`,
        removeSlot: (companyId, bookingId, slotId) => `/app/${companyId}/wellness/booking/${bookingId}/slots/${slotId}`,
        updateSlotDetails: (companyId, bookingId, slotId) => `/app/${companyId}/wellness/booking/${bookingId}/slots/${slotId}/details`,
        slots: (companyId, bookingId) => `/app/${companyId}/wellness/booking/${bookingId}/slots`,
        canDelete: (companyId, bookingId) => `/app/${companyId}/wellness/booking/${bookingId}/can-delete`,
        delete: (companyId, bookingId) => `/app/${companyId}/wellness/booking/${bookingId}`,
        status: (companyId, bookingId) => `/app/${companyId}/wellness/booking/status/${bookingId}`,
        details: (companyId, bookingId) => `/app/${companyId}/wellness/booking/details/${bookingId}`,
        searchAll: (companyId) => `/app/${companyId}/wellness/booking/search-all`,
        timeline: (companyId) => `/app/${companyId}/wellness/booking/timeline`,
        planning: (companyId, bookingId) => `/app/${companyId}/wellness/booking/planning/${bookingId}`,
        dailyPlanning: (companyId) => `/app/${companyId}/wellness/booking/daily-planning`,
        updateCustomer: (companyId, bookingId) => `/app/${companyId}/wellness/booking/${bookingId}/customer`,
        groupSlots: (companyId, groupSessionId) => `/app/${companyId}/wellness/booking/group-slots/${groupSessionId}`,
        updateGroup: (companyId, groupSessionId) => `/app/${companyId}/wellness/booking/group/${groupSessionId}/update`,
        removeParticipantFromGroup: (companyId, groupSessionId, slotId) => `/app/${companyId}/wellness/booking/group/${groupSessionId}/participant/${slotId}`,
        reschedule: (companyId, bookingId) => `/app/${companyId}/wellness/booking/${bookingId}/reschedule`,
      },
      availability: {
        sharedSpaceOccupancy: (companyId) => `/app/${companyId}/wellness/availability/shared-space-occupancy`,
      },
      transaction: {
        update: (companyId, transactionId) => `/app/${companyId}/wellness/transaction/${transactionId}`,
        updateComplete: (companyId, transactionId) => `/app/${companyId}/wellness/transaction/${transactionId}/update-complete`,
        generateInvoice: (companyId, transactionId) => `/app/${companyId}/wellness/transaction/${transactionId}/invoice`,
      },
      payment: {
        add: (companyId, bookingId) => `/app/${companyId}/wellness/payment/${bookingId}/add`,
        cancel: (companyId, paymentId) => `/app/${companyId}/wellness/payment/${paymentId}/cancel`,
        history: (companyId, bookingId) => `/app/${companyId}/wellness/payment/${bookingId}/history`,
      },
      dailyClosings: {
        getAll: (companyId) => `/app/${companyId}/wellness/daily-closings`,
        getByDate: (companyId, date) => `/app/${companyId}/wellness/daily-closings/date/${date}`,
        getDetailedBreakdown: (companyId, date) => `/app/${companyId}/wellness/daily-closings/date/${date}/detailed-breakdown`,
        export: (companyId, closingId) => `/app/${companyId}/wellness/daily-closings/${closingId}/export`,
        posStats: (companyId) => `/app/${companyId}/wellness/daily-closings/pos-stats`,
        create: (companyId) => `/app/${companyId}/wellness/daily-closings/create`,
        close: (companyId) => `/app/${companyId}/wellness/daily-closings/close`,
        reopen: (companyId) => `/app/${companyId}/wellness/daily-closings/reopen`,
      },
      dailyMemo: {
        all: (companyId) => `/app/${companyId}/wellness/daily-memo/all`,
        create: (companyId) => `/app/${companyId}/wellness/daily-memo/create`,
        update: (companyId, memoId) => `/app/${companyId}/wellness/daily-memo/update/${memoId}`,
        delete: (companyId, memoId) => `/app/${companyId}/wellness/daily-memo/delete/${memoId}`,
      },
    },
    newsletter: {
      campaigns: (companyId) => `/app/${companyId}/newsletter/campaigns`,
      details: (companyId, campaignId) => `/app/${companyId}/newsletter/campaigns/${campaignId}`,
      preview: (companyId, campaignId) => `/app/${companyId}/newsletter/campaigns/${campaignId}/preview`,
      progress: (companyId, campaignId) => `/app/${companyId}/newsletter/campaigns/${campaignId}/progress`,
      send: (companyId, campaignId) => `/app/${companyId}/newsletter/campaigns/${campaignId}/send`,
      duplicate: (companyId, campaignId) => `/app/${companyId}/newsletter/campaigns/${campaignId}/duplicate`,
      images: (companyId) => `/app/${companyId}/newsletter/campaigns/images`,
      templates: (companyId) => `/app/${companyId}/newsletter/campaigns/templates`,
      overview: (companyId) => `/app/${companyId}/newsletter/campaigns/overview`,
    },
    restaurant: {
      area: {
        all: (companyId) => `/app/${companyId}/restaurant/area/all`,
        create: (companyId) => `/app/${companyId}/restaurant/area/create`,
        duplicate: (companyId, areaId) => `/app/${companyId}/restaurant/area/duplicate/${areaId}`,
        update: (companyId, areaId) => `/app/${companyId}/restaurant/area/update/${areaId}`,
        delete: (companyId, areaId) => `/app/${companyId}/restaurant/area/delete/${areaId}`,
        reorder: (companyId) => `/app/${companyId}/restaurant/area/reorder`,
      },
      table: {
        all: (companyId) => `/app/${companyId}/restaurant/table/all`,
        create: (companyId) => `/app/${companyId}/restaurant/table/create`,
        update: (companyId, tableId) => `/app/${companyId}/restaurant/table/update/${tableId}`,
        delete: (companyId, tableId) => `/app/${companyId}/restaurant/table/delete/${tableId}`,
        deleteMultiple: (companyId) => `/app/${companyId}/restaurant/table/delete-multiple`,
        duplicate: (companyId, tableId) => `/app/${companyId}/restaurant/table/duplicate/${tableId}`,
      },
      tableCombination: {
        all: (companyId) => `/app/${companyId}/restaurant/table-combination/all`,
        create: (companyId) => `/app/${companyId}/restaurant/table-combination/create`,
        update: (companyId, combinationId) => `/app/${companyId}/restaurant/table-combination/update/${combinationId}`,
        delete: (companyId, combinationId) => `/app/${companyId}/restaurant/table-combination/delete/${combinationId}`,
      },
      tableBlock: {
        toggle: (companyId) => `/app/${companyId}/restaurant/table-blocks/toggle`,
        list: (companyId) => `/app/${companyId}/restaurant/table-blocks/all`,
      },
      tableColor: {
        set: (companyId) => `/app/${companyId}/restaurant/table-colors/set`,
        remove: (companyId) => `/app/${companyId}/restaurant/table-colors/remove`,
        list: (companyId) => `/app/${companyId}/restaurant/table-colors/all`,
      },
      dayMemo: {
        list: (companyId) => `/app/${companyId}/restaurant/day-memos/all`,
        create: (companyId) => `/app/${companyId}/restaurant/day-memos/create`,
        update: (companyId, memoId) => `/app/${companyId}/restaurant/day-memos/update/${memoId}`,
        delete: (companyId, memoId) => `/app/${companyId}/restaurant/day-memos/delete/${memoId}`,
      },
      floorPlanElement: {
        all: (companyId) => `/app/${companyId}/restaurant/floor-plan-element/all`,
        create: (companyId) => `/app/${companyId}/restaurant/floor-plan-element/create`,
        update: (companyId, elementId) => `/app/${companyId}/restaurant/floor-plan-element/update/${elementId}`,
        delete: (companyId, elementId) => `/app/${companyId}/restaurant/floor-plan-element/delete/${elementId}`,
      },
      availability: {
        dates: (companyId) => `/app/${companyId}/restaurant/availability/dates`,
        timeslots: (companyId) => `/app/${companyId}/restaurant/availability/timeslots`,
        tablesStatus: (companyId) => `/app/${companyId}/restaurant/availability/tables-status`,
      },
      reservationFull: {
        getStatus: (companyId) => `/app/${companyId}/restaurant/reservation-full/status`,
        markServiceAsFull: (companyId) => `/app/${companyId}/restaurant/reservation-full/service`,
        markTimeslotAsFull: (companyId) => `/app/${companyId}/restaurant/reservation-full/timeslot`,
        removeServiceFullStatus: (companyId) => `/app/${companyId}/restaurant/reservation-full/service`,
        removeTimeslotFullStatus: (companyId) => `/app/${companyId}/restaurant/reservation-full/timeslot`,
        getAllPossibleTimeslots: (companyId) => `/app/${companyId}/restaurant/reservation-full/dashboard/timeslots`,
        getAllServicesForDate: (companyId) => `/app/${companyId}/restaurant/reservation-full/dashboard/services`,
      },
      reservation: {
        create: (companyId) => `/app/${companyId}/restaurant/reservation/create`,
        cardHoldEvaluation: (companyId) => `/app/${companyId}/restaurant/reservation/card-hold-evaluation`,
        accept: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/accept/${reservationId}`,
        update: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/${reservationId}`,
        cancel: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/cancel/${reservationId}`,
        delete: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/${reservationId}`,
        details: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/details/${reservationId}`,
        search: (companyId) => `/app/${companyId}/restaurant/reservation/search`,
        monthlyStats: (companyId) => `/app/${companyId}/restaurant/reservation/monthly-stats`,
        day: (companyId) => `/app/${companyId}/restaurant/reservation/day`,
        validateChanges: (companyId) => `/app/${companyId}/restaurant/reservation/validate-changes`,
        cancellationStatus: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/${reservationId}/cancellation-status`,
        resendEmail: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/${reservationId}/resend-email`,
        lockTable: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/${reservationId}/lock-table`,
        payment: {
          capture: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/${reservationId}/payment/capture`,
          cancel: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/${reservationId}/payment/cancel`,
          retryCharge: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/${reservationId}/payment/retry-charge`,
          status: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/${reservationId}/payment`,
          resendNotification: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/${reservationId}/payment/resend-notification`,
          requestCardHold: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/${reservationId}/payment/request-card-hold`,
        },
        emailLogs: {
          list: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/${reservationId}/email-logs`,
          preview: (companyId, reservationId, emailLogId) => `/app/${companyId}/restaurant/reservation/${reservationId}/email-logs/${emailLogId}/preview`,
        },
        smsLogs: {
          list: (companyId, reservationId) => `/app/${companyId}/restaurant/reservation/${reservationId}/sms-logs`,
        },
        waitlist: {
          update: (companyId, entryId) => `/app/${companyId}/restaurant/waitlist/update/${entryId}`,
          promote: (companyId, entryId) => `/app/${companyId}/restaurant/waitlist/promote/${entryId}`,
          remove: (companyId, entryId) => `/app/${companyId}/restaurant/waitlist/delete/${entryId}`,
        },
        draft: {
          list: (companyId) => `/app/${companyId}/restaurant/reservation-draft/all`,
          count: (companyId) => `/app/${companyId}/restaurant/reservation-draft/count`,
        },
      },
      services: {
        get: (companyId) => `/app/${companyId}/restaurant/restaurant-config/get`,
        create: (companyId) => `/app/${companyId}/restaurant/restaurant-config/create`,
        update: (companyId) => `/app/${companyId}/restaurant/restaurant-config/update`,
        emergencyLink: (companyId) => `/app/${companyId}/restaurant/restaurant-config/emergency-link`,
      },
      reviews: {
        all: (companyId) => `/app/${companyId}/restaurant/reviews/all`,
        statistics: (companyId) => `/app/${companyId}/restaurant/reviews/statistics`,
      },
      // Messagerie client ↔ établissement (une discussion par réservation).
      messages: {
        conversations: (companyId) => `/app/${companyId}/restaurant/messages/conversations`,
        unreadSummary: (companyId) => `/app/${companyId}/restaurant/messages/unread-summary`,
        bulk: (companyId) => `/app/${companyId}/restaurant/messages/conversations/bulk`,
        // Fil de la discussion : GET pour le lire, POST pour répondre, DELETE pour supprimer la discussion
        thread: (companyId, reservationId) => `/app/${companyId}/restaurant/messages/reservation/${reservationId}`,
        read: (companyId, reservationId) => `/app/${companyId}/restaurant/messages/reservation/${reservationId}/read`,
        unread: (companyId, reservationId) => `/app/${companyId}/restaurant/messages/reservation/${reservationId}/unread`,
        favorite: (companyId, reservationId) => `/app/${companyId}/restaurant/messages/reservation/${reservationId}/favorite`,
      },
      formula: {
        all: (companyId) => `/app/${companyId}/restaurant/formula/all`,
        create: (companyId) => `/app/${companyId}/restaurant/formula/create`,
        update: (companyId, formulaId) => `/app/${companyId}/restaurant/formula/update/${formulaId}`,
        delete: (companyId, formulaId) => `/app/${companyId}/restaurant/formula/delete/${formulaId}`,
        reorder: (companyId) => `/app/${companyId}/restaurant/formula/reorder`,
      },
    },
    dashboard: {
      layout: (companyId) => `/app/${companyId}/dashboard/layout`,
    },
    analytics: {
      restaurant: {
        all: (companyId, startDate = null, endDate = null, granularity = null) => {
          const params = new URLSearchParams();
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          if (granularity) params.append('granularity', granularity);
          const qs = params.toString();
          return `/app/${companyId}/analytics/restaurant/all${qs ? `?${qs}` : ''}`;
        },
        today: (companyId) => `/app/${companyId}/analytics/restaurant/today`,
        welcomeSeries: (companyId) => `/app/${companyId}/analytics/restaurant/welcome-series`,
        recentReservations: (companyId, page = 1) =>
          `/app/${companyId}/analytics/restaurant/recent-reservations?page=${page}`,
        modificationJournal: (companyId, page = 1) =>
          `/app/${companyId}/analytics/restaurant/modification-journal?page=${page}`,
        overview: (companyId, startDate = null, endDate = null) => {
          const params = new URLSearchParams();
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          const qs = params.toString();
          return `/app/${companyId}/analytics/restaurant/overview${qs ? `?${qs}` : ''}`;
        },
        historical: (companyId, startDate = null, endDate = null, granularity = 'month') => {
          const params = new URLSearchParams({granularity});
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          return `/app/${companyId}/analytics/restaurant/historical?${params}`;
        },
        services: (companyId, startDate = null, endDate = null) => {
          const params = new URLSearchParams();
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          const qs = params.toString();
          return `/app/${companyId}/analytics/restaurant/services${qs ? `?${qs}` : ''}`;
        },
        hourly: (companyId, startDate = null, endDate = null) => {
          const params = new URLSearchParams();
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          const qs = params.toString();
          return `/app/${companyId}/analytics/restaurant/hourly${qs ? `?${qs}` : ''}`;
        },
        dayOfWeek: (companyId, startDate = null, endDate = null) => {
          const params = new URLSearchParams();
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          const qs = params.toString();
          return `/app/${companyId}/analytics/restaurant/day-of-week${qs ? `?${qs}` : ''}`;
        },
        monthly: (companyId, months = 12) =>
          `/app/${companyId}/analytics/restaurant/monthly?months=${months}`,
        compare: (companyId, period1Start, period1End, period2Start, period2End) =>
          `/app/${companyId}/analytics/restaurant/compare?period1Start=${period1Start}&period1End=${period1End}&period2Start=${period2Start}&period2End=${period2End}`,
        topCustomers: (companyId, startDate = null, endDate = null, limit = 10) => {
          const params = new URLSearchParams({limit: limit.toString()});
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          return `/app/${companyId}/analytics/restaurant/top-customers?${params}`;
        },
      },
      giftVoucher: {
        all: (companyId) => `/app/${companyId}/analytics/gift-voucher/all`,
        compare: (companyId, period1Start, period1End, period2Start, period2End) =>
          `/app/${companyId}/analytics/gift-voucher/compare?period1Start=${period1Start}&period1End=${period1End}&period2Start=${period2Start}&period2End=${period2End}`,
        businessKpis: (companyId, startDate = null, endDate = null) => {
          const params = new URLSearchParams();
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          const qs = params.toString();
          return `/app/${companyId}/analytics/gift-voucher/business-kpis${qs ? `?${qs}` : ''}`;
        },
        topCustomers: (companyId, startDate = null, endDate = null, limit = 10) => {
          const params = new URLSearchParams({limit: limit.toString()});
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          return `/app/${companyId}/analytics/gift-voucher/top-customers?${params}`;
        },
        monthlyBreakdown: (companyId, months = 12) =>
          `/app/${companyId}/analytics/gift-voucher/monthly-breakdown?months=${months}`,
      },
    },
    smsWallet: {
      balance: (companyId) => `/app/${companyId}/sms-wallet/balance`,
      pricing: (companyId) => `/app/${companyId}/sms-wallet/pricing`,
      transactions: (companyId) => `/app/${companyId}/sms-wallet/transactions`,
      createPaymentIntent: (companyId) => `/app/${companyId}/sms-wallet/payment-intent`,
    },
    giftVoucherType: {
      all: (companyId) => `/app/${companyId}/catalog/all`,
      details: (companyId, typeId) => `/app/${companyId}/catalog/details/${typeId}`,
      create: (companyId) => `/app/${companyId}/catalog/create`,
      update: (companyId, typeId) => `/app/${companyId}/catalog/update/${typeId}`,
      delete: (companyId, typeId) => `/app/${companyId}/catalog/delete/${typeId}`,
      deleteMultiple: (companyId) => `/app/${companyId}/catalog/delete-multiple`,
      archive: (companyId) => `/app/${companyId}/catalog/archive`,
      unarchive: (companyId) => `/app/${companyId}/catalog/unarchive`,
      updateStatus: (companyId) => `/app/${companyId}/catalog/status`,
      reorder: (companyId, typeId) => `/app/${companyId}/catalog/reorder/${typeId}`,
      updateSharing: (companyId, typeId) => `/app/${companyId}/catalog/sharing/${typeId}`,
    },
    catalogCategory: {
      all: (companyId) => `/app/${companyId}/catalog-categories/all`,
      details: (companyId, categoryId) => `/app/${companyId}/catalog-categories/details/${categoryId}`,
      create: (companyId) => `/app/${companyId}/catalog-categories/create`,
      update: (companyId, categoryId) => `/app/${companyId}/catalog-categories/update/${categoryId}`,
      delete: (companyId, categoryId) => `/app/${companyId}/catalog-categories/delete/${categoryId}`,
      reorder: (companyId, categoryId) => `/app/${companyId}/catalog-categories/reorder/${categoryId}`,
    },
    catalogOption: {
      all: (companyId) => `/app/${companyId}/catalog-options/all`,
      details: (companyId, optionId) => `/app/${companyId}/catalog-options/details/${optionId}`,
      create: (companyId) => `/app/${companyId}/catalog-options/create`,
      update: (companyId, optionId) => `/app/${companyId}/catalog-options/update/${optionId}`,
      delete: (companyId, optionId) => `/app/${companyId}/catalog-options/delete/${optionId}`,
    },
    giftVoucher: {
      all: (companyId) => `/app/${companyId}/gift-voucher/all`,
      details: (companyId, giftVoucherId) => `/app/${companyId}/gift-voucher/details/${giftVoucherId}`,
      createManual: (companyId) => `/app/${companyId}/gift-voucher/create-manual`,
      update: (companyId, giftVoucherId) => `/app/${companyId}/gift-voucher/update/${giftVoucherId}`,
      lookup: (companyId) => `/app/${companyId}/gift-voucher/lookup`,
      templatePreview: (companyId, templateName) => `/app/${companyId}/gift-voucher/template-preview/${templateName}`,
      resendEmail: (companyId, giftVoucherId) => `/app/${companyId}/gift-voucher/resend-email/${giftVoucherId}`,
      confirmPostalShipping: (companyId, giftVoucherId) => `/app/${companyId}/gift-voucher/confirm-postal-shipping/${giftVoucherId}`,
      generateInvoice: (companyId, giftVoucherId) => `/app/${companyId}/gift-voucher/generate-invoice/${giftVoucherId}`,
      expirationReminders: {
        preview: (companyId) => `/app/${companyId}/gift-voucher/expiration-reminders/preview`,
        emailPreview: (companyId) => `/app/${companyId}/gift-voucher/expiration-reminders/email-preview`,
        send: (companyId) => `/app/${companyId}/gift-voucher/expiration-reminders/send`,
      },
      settings: {
        get: (companyId) => `/app/${companyId}/gift-voucher/settings/get`,
        update: (companyId) => `/app/${companyId}/gift-voucher/settings/update`,
      },
    },
    promotions: {
      all: (companyId) => `/app/${companyId}/promotions/all`,
      details: (companyId, promotionId) => `/app/${companyId}/promotions/details/${promotionId}`,
      stats: (companyId, promotionId) => `/app/${companyId}/promotions/stats/${promotionId}`,
      create: (companyId) => `/app/${companyId}/promotions/create`,
      update: (companyId, promotionId) => `/app/${companyId}/promotions/update/${promotionId}`,
      delete: (companyId, promotionId) => `/app/${companyId}/promotions/delete/${promotionId}`,
    },
    transaction: {
      all: (companyId) => `/app/${companyId}/transaction/all`,
    },
    invoice: {
      all: (companyId) => `/app/${companyId}/invoice/all`,
      lastTen: (companyId) => `/app/${companyId}/invoice/last-ten`,
      lastSixMonths: (companyId) => `/app/${companyId}/invoice/last-six-months`,
      b2bClients: (companyId) => `/app/${companyId}/invoice/b2b-clients`,
    },
    subscription: {
      planByModuleAndTier: (companyId, module, tier) => `/app/${companyId}/subscription/plans/${module}/${tier}`,
      current: (companyId, module = null) => module ?
        `/app/${companyId}/subscription/current?module=${module}` :
        `/app/${companyId}/subscription/current`,
      all: (companyId) => `/app/${companyId}/subscription/all`,
      create: (companyId) => `/app/${companyId}/subscription/create`,
      validateCoupon: (companyId) => `/app/${companyId}/subscription/validate-coupon`,
      cancel: (companyId, subscriptionId) => `/app/${companyId}/subscription/${subscriptionId}/cancel`,
      reactivate: (companyId, subscriptionId) => `/app/${companyId}/subscription/${subscriptionId}/reactivate`,
      upgrade: (companyId, subscriptionId) => `/app/${companyId}/subscription/${subscriptionId}/upgrade`,
      downgrade: (companyId, subscriptionId) => `/app/${companyId}/subscription/${subscriptionId}/downgrade`,
      cancelScheduledDowngrade: (companyId, subscriptionId) => `/app/${companyId}/subscription/${subscriptionId}/scheduled-downgrade`,
      sync: (companyId, subscriptionId) => `/app/${companyId}/subscription/${subscriptionId}/sync`,
      convertToPaid: (companyId, subscriptionId) => `/app/${companyId}/subscription/${subscriptionId}/convert-to-paid`,
      paymentMethods: {
        list: (companyId) => `/app/${companyId}/subscription/payment-methods`,
        add: (companyId) => `/app/${companyId}/subscription/payment-methods`,
        delete: (companyId, paymentMethodId) => `/app/${companyId}/subscription/payment-methods/${paymentMethodId}`,
        setDefault: (companyId, paymentMethodId) => `/app/${companyId}/subscription/payment-methods/${paymentMethodId}/default`,
      },
      retryAllPayments: (companyId) => `/app/${companyId}/subscription/retry-all-payments`,
    },
    modules: {
      all: (companyId, options = {}) => {
        const params = new URLSearchParams();
        if (options.includePlans !== undefined) params.append('include_plans', options.includePlans);
        const queryString = params.toString();
        return `/app/${companyId}/modules/all${queryString ? `?${queryString}` : ''}`;
      },
      details: (companyId, moduleId, options = {}) => {
        const params = new URLSearchParams();
        if (options.includePlans !== undefined) params.append('include_plans', options.includePlans);
        const queryString = params.toString();
        return `/app/${companyId}/modules/${moduleId}${queryString ? `?${queryString}` : ''}`;
      },
      plans: (companyId, moduleId) => `/app/${companyId}/modules/${moduleId}/plans`,
      exists: (companyId, moduleId) => `/app/${companyId}/modules/${moduleId}/exists`,
    },
    notificationSettings: {
      get: (companyId) => `/app/${companyId}/notification-settings/get`,
      update: (companyId) => `/app/${companyId}/notification-settings/update`,
    },
    communicationContent: {
      registry: (companyId) => `/app/${companyId}/communication-content/registry`,
      get: (companyId) => `/app/${companyId}/communication-content/get`,
      update: (companyId) => `/app/${companyId}/communication-content/update`,
      previewEmail: (companyId, contentKey) => `/app/${companyId}/communication-content/emails/${contentKey}/preview`,
      previewSms: (companyId, smsKey) => `/app/${companyId}/communication-content/sms/${smsKey}/preview`,
    },
    notifications: {
      get: (companyId) => `/app/${companyId}/notifications/get`,
      unreadCount: (companyId) => `/app/${companyId}/notifications/unread-count`,
      markAllRead: (companyId) => `/app/${companyId}/notifications/mark-all-read`,
      toggleRead: (companyId, notificationId) => `/app/${companyId}/notifications/${notificationId}/toggle-read`,
    },
  },
  public: {
    company: {
      details: '/public/company/details',
    },
    newsletter: {
      // Le jeton voyage en query : c'est l'URL que porte l'en-tete List-Unsubscribe.
      unsubscribeContext: (token) => `/public/newsletter/unsubscribe/context?token=${encodeURIComponent(token)}`,
      unsubscribe: (token) => `/public/newsletter/unsubscribe?token=${encodeURIComponent(token)}`,
    },
    catalog: {
      all: '/public/catalog/all',
      details: '/public/catalog/details/:companyId/:giftVoucherTypeId',
      calculatePrice: '/public/catalog/calculate-price/:companyId',
      calculateCompositionPrice: '/public/catalog/calculate-composition-price/:companyId',
    },
    catalogCategories: {
      all: (companyId) => `/public/catalog-categories/all/${companyId}`,
      details: (companyId, categoryId) => `/public/catalog-categories/details/${companyId}/${categoryId}`,
    },
    catalogOptions: {
      compatible: (companyId, giftVoucherTypeId) => `/public/catalog-options/compatible/${companyId}/${giftVoucherTypeId}`,
      compatibleCustomVoucher: (companyId) => `/public/catalog-options/compatible-custom-voucher/${companyId}`,
      details: (companyId, optionId) => `/public/catalog-options/details/${companyId}/${optionId}`,
    },
    giftVoucher: {
      createPaymentIntent: '/public/purchase/create-payment-intent',
      createOrder: '/public/purchase/create-gift-voucher',
      updateOrder: '/public/purchase/update-gift-voucher',
      transactionDetails: '/public/purchase/transaction-details/:transactionId',
    },
    commission: {
      calculate: '/public/commission/calculate/:companyId/:baseAmount',
    },
    giftVoucherSettings: {
      postalAvailability: (companyId) => `/public/gift-voucher-settings/postal/availability/${companyId}`,
      postalCountries: (companyId) => `/public/gift-voucher-settings/postal/countries/${companyId}`,
      customVoucherConfig: (companyId) => `/public/gift-voucher-settings/custom-voucher/config/${companyId}`,
      b2bInvoicingAvailability: (companyId) => `/public/gift-voucher-settings/b2b-invoicing/availability/${companyId}`,
      catalogPage: (companyId) => `/public/gift-voucher-settings/catalog-page/${companyId}`,
    },
    promotions: {
      validate: (companyId) => `/public/promotions/validate/${companyId}`,
      checkApplicable: (companyId) => `/public/promotions/check-applicable/${companyId}`,
    },
    reviews: {
      get: (token) => `/public/restaurant/reviews/${token}`,
      submit: (token) => `/public/restaurant/reviews/${token}`,
    },
    restaurant: {
      reservation: {
        // Création d'une réservation (par companyId / slug, sans clé API).
        config: (companyId) => `/public/restaurant/reservation/${companyId}/config`,
        dates: (companyId) => `/public/restaurant/reservation/${companyId}/dates`,
        nextDates: (companyId) => `/public/restaurant/reservation/${companyId}/next-dates`,
        timeslots: (companyId) => `/public/restaurant/reservation/${companyId}/timeslots`,
        rooms: (companyId) => `/public/restaurant/reservation/${companyId}/rooms`,
        create: (companyId) => `/public/restaurant/reservation/${companyId}/create`,
        evaluateCardHold: (companyId) => `/public/restaurant/reservation/${companyId}/evaluate-card-hold`,
        preparePayment: (companyId) => `/public/restaurant/reservation/${companyId}/prepare-payment`,
        confirmPayment: (companyId) => `/public/restaurant/reservation/${companyId}/confirm-payment`,
        waitlist: (companyId) => `/public/restaurant/reservation/${companyId}/waitlist`,
        draft: (companyId) => `/public/restaurant/reservation/${companyId}/draft`,
        formula: {
          available: (companyId) => `/public/restaurant/reservation/${companyId}/formula/available`,
          dates: (companyId) => `/public/restaurant/reservation/${companyId}/formula/dates`,
        },
        // Gestion d'une réservation existante (par token signé, lien email).
        confirmationData: (token) => `/public/restaurant/reservation/confirmation-data/${token}`,
        manage: (token) => `/public/restaurant/reservation/manage/${token}`,
        update: (token) => `/public/restaurant/reservation/manage/${token}`,
        cancel: (token) => `/public/restaurant/reservation/manage/${token}/cancel`,
        messages: {
          thread: (token) => `/public/restaurant/reservation/manage/${token}/messages`,
          read: (token) => `/public/restaurant/reservation/manage/${token}/messages/read`,
        },
        payment: {
          status: (token) => `/public/restaurant/reservation/${token}/payment/status`,
          setup: (token) => `/public/restaurant/reservation/${token}/payment/setup`,
          confirm: (token) => `/public/restaurant/reservation/${token}/payment/confirm`,
          retry: (token) => `/public/restaurant/reservation/${token}/payment/retry`,
        },
        cardHoldRequest: {
          status: (token) => `/public/restaurant/reservation/${token}/card-hold-request/status`,
          setup: (token) => `/public/restaurant/reservation/${token}/card-hold-request/setup`,
          confirm: (token) => `/public/restaurant/reservation/${token}/card-hold-request/confirm`,
        },
      },
    },
    maintenance: {
      check: '/public/maintenance/check',
    },
    validation: {
      emailDomain: (domain) => `/public/validate/email-domain?domain=${encodeURIComponent(domain)}`,
    },
  },
  admin: {
    // Merchants (Établissements)
    subscriptions: {
      companies: '/admin/merchants/subscriptions/companies',
      modules: '/admin/merchants/subscriptions/modules',
      plans: '/admin/merchants/subscriptions/plans',
      companyDetails: (companyId) => `/admin/merchants/subscriptions/companies/${companyId}`,
      grant: '/admin/merchants/subscriptions/grant',
      updateGrant: (subscriptionId) => `/admin/merchants/subscriptions/${subscriptionId}/grant`,
      revoke: (subscriptionId) => `/admin/merchants/subscriptions/${subscriptionId}/revoke`,
      cancel: (subscriptionId) => `/admin/merchants/subscriptions/${subscriptionId}/cancel`,
      changePlan: (subscriptionId) => `/admin/merchants/subscriptions/${subscriptionId}/change-plan`,
      retryPayment: (subscriptionId) => `/admin/merchants/subscriptions/${subscriptionId}/retry-payment`,
      applyCoupon: (subscriptionId) => `/admin/merchants/subscriptions/${subscriptionId}/coupon`,
      removeCoupon: (subscriptionId) => `/admin/merchants/subscriptions/${subscriptionId}/coupon`,
      payments: (subscriptionId) => `/admin/merchants/subscriptions/${subscriptionId}/payments`,
      freeMonths: (subscriptionId) => `/admin/merchants/subscriptions/${subscriptionId}/free-months`,
      creditBalance: (subscriptionId) => `/admin/merchants/subscriptions/${subscriptionId}/credit-balance`,
      stats: '/admin/merchants/subscriptions/stats',
      revenueHistory: '/admin/merchants/subscriptions/revenue-history',
      subscribedCompanies: '/admin/merchants/subscriptions/subscribed-companies',
      pendingPayments: '/admin/merchants/subscriptions/pending-payments',
      expiringGrants: '/admin/merchants/subscriptions/expiring-grants',
      upcomingCancellations: '/admin/merchants/subscriptions/upcoming-cancellations',
      churnStats: (months = 6) => `/admin/merchants/subscriptions/churn-stats?months=${months}`,
      trialExpiredNotice: (subscriptionId) => `/admin/merchants/subscriptions/${subscriptionId}/trial-expired-notice`,
    },
    companies: {
      all: '/admin/merchants/companies/all',
      options: '/admin/merchants/companies/options',
      create: '/admin/merchants/companies/create',
      details: (id) => `/admin/merchants/companies/details/${id}`,
      update: (id) => `/admin/merchants/companies/update/${id}`,
      analytics: '/admin/merchants/companies/analytics',
      delete: (id) => `/admin/merchants/companies/delete/${id}`,
      stripeConnectUrl: (id) => `/admin/merchants/companies/stripe-connect-url/${id}`,
      restaurantOpening: (id) => `/admin/merchants/companies/restaurant-opening/${id}`,
      integrationKeys: (id) => `/admin/merchants/companies/${id}/integration-keys`,
      revokeIntegrationKey: (id, keyId) => `/admin/merchants/companies/${id}/integration-keys/${keyId}`,
      purgeIntegrationKey: (id, keyId) => `/admin/merchants/companies/${id}/integration-keys/${keyId}/purge`,
    },
    accounts: {
      all: '/admin/merchants/accounts/all',
      details: (id) => `/admin/merchants/accounts/details/${id}`,
      search: '/admin/merchants/accounts/search',
      stats: '/admin/merchants/accounts/stats',
      createUser: (accountId) => `/admin/merchants/accounts/${accountId}/users`,
      addCompany: (accountId) => `/admin/merchants/accounts/${accountId}/companies`,
      removeCompany: (accountId, companyId) => `/admin/merchants/accounts/${accountId}/companies/${companyId}`,
      grantAccess: (accountId, userId, companyId) => `/admin/merchants/accounts/${accountId}/users/${userId}/companies/${companyId}`,
      revokeAccess: (accountId, userId, companyId) => `/admin/merchants/accounts/${accountId}/users/${userId}/companies/${companyId}`,
      enableCustomerSharing: (accountId) => `/admin/merchants/accounts/${accountId}/enable-customer-sharing`,
    },
    access: {
      grant: '/admin/merchants/access/grant',
      revoke: '/admin/merchants/access/revoke',
      status: '/admin/merchants/access/status',
      history: (userId) => `/admin/merchants/access/history/${userId}`,
      historyGlobal: '/admin/merchants/access/history-global',
      users: (companyId) => `/admin/merchants/access/users/${companyId}`,
    },

    giftVoucher: {
      all: '/admin/analytics/gift-vouchers/all',
      details: (id) => `/admin/analytics/gift-vouchers/details/${id}`,
      analytics: {
        counters: '/admin/analytics/gift-vouchers/counters',
        performance: '/admin/analytics/gift-vouchers/performance',
        monthly: '/admin/analytics/gift-vouchers/monthly',
        compare: '/admin/analytics/gift-vouchers/compare',
        commissionsRevenue: (year, month) => `/admin/analytics/gift-vouchers/commissions-revenue/${year}/${month}`,
        topCompanies: '/admin/analytics/gift-vouchers/top-companies',
      },
    },

    reservations: {
      overview: '/admin/analytics/reservations/overview',
      weeklyTrend: '/admin/analytics/reservations/weekly-trend',
      sources: '/admin/analytics/reservations/sources',
      topCompanies: '/admin/analytics/reservations/top-companies',
      dailyTrend: '/admin/analytics/reservations/daily-trend',
      monthlyEvolution: '/admin/analytics/reservations/monthly-evolution',
      onlineVsBackoffice: '/admin/analytics/reservations/online-vs-backoffice',
      services: '/admin/analytics/reservations/services',
    },

    invoices: {
      all: '/admin/finances/invoices/all',
      details: (id) => `/admin/finances/invoices/details/${id}`,
      company: (companyId) => `/admin/finances/invoices/company/${companyId}`,
      stats: '/admin/finances/invoices/stats',
      monthly: (year, month) => `/admin/finances/invoices/monthly/${year}/${month}`,
      downloadZip: '/admin/finances/invoices/download-zip',
    },

    smsLogs: {
      stats: '/admin/communications/sms-logs/stats',
      margin: '/admin/communications/sms-logs/margin',
      balances: '/admin/communications/sms-logs/balances',
      all: '/admin/communications/sms-logs/all',
    },

    crm: {
      prospects: {
        all: '/admin/crm/prospects/all',
        kanban: '/admin/crm/prospects/kanban',
        details: (id) => `/admin/crm/prospects/details/${id}`,
        create: '/admin/crm/prospects/create',
        update: (id) => `/admin/crm/prospects/update/${id}`,
        updateStatus: (id) => `/admin/crm/prospects/${id}/status`,
        delete: (id) => `/admin/crm/prospects/delete/${id}`,
        convert: (id) => `/admin/crm/prospects/${id}/convert`,
        stats: '/admin/crm/prospects/stats',
        importFields: '/admin/crm/prospects/import/fields',
        importAnalyze: '/admin/crm/prospects/import/analyze',
        importExecute: '/admin/crm/prospects/import/execute',
        activities: {
          list: (prospectId) => `/admin/crm/prospects/${prospectId}/activities`,
          count: (prospectId) => `/admin/crm/prospects/${prospectId}/activities/count`,
          stats: (prospectId) => `/admin/crm/prospects/${prospectId}/activities/stats`,
          create: (prospectId) => `/admin/crm/prospects/${prospectId}/activities`,
          update: (activityId) => `/admin/crm/prospects/activities/${activityId}`,
          delete: (activityId) => `/admin/crm/prospects/activities/${activityId}`,
          details: (activityId) => `/admin/crm/prospects/activities/${activityId}/details`,
          upcoming: '/admin/crm/prospects/activities/upcoming/all',
        },
      },
    },

    users: {
      all: '/admin/users/all',
    },

    commissions: {
      overview: '/admin/finances/commissions/overview',
      monthly: '/admin/finances/commissions/monthly',
      byCompany: '/admin/finances/commissions/by-company',
      partners: '/admin/finances/commissions/partners',
      balance: '/admin/finances/commissions/balance',
      payouts: '/admin/finances/commissions/payouts',
      unpaidMonths: '/admin/finances/commissions/payouts/unpaid-months',
      deletePayout: (payoutId) => `/admin/finances/commissions/payouts/${payoutId}`,
      charges: '/admin/finances/commissions/charges',
      endCharge: (chargeId) => `/admin/finances/commissions/charges/${chargeId}/end`,
      deleteCharge: (chargeId) => `/admin/finances/commissions/charges/${chargeId}`,
    },

    team: {
      all: '/admin/team/all',
      create: '/admin/team/create',
      update: (userId) => `/admin/team/${userId}`,
      status: (userId) => `/admin/team/${userId}/status`,
      portfolio: (userId) => `/admin/team/${userId}/portfolio`,
    },

    emailLogs: {
      all: '/admin/communications/email-logs/all',
      details: (id) => `/admin/communications/email-logs/details/${id}`,
      company: (companyId) => `/admin/communications/email-logs/company/${companyId}`,
      stats: '/admin/communications/email-logs/stats',
      templateTypes: '/admin/communications/email-logs/template-types',
    },

    newsletterQuotas: {
      overview: '/admin/communications/newsletter-quotas/overview',
    },

    newsletterSendingAccounts: {
      state: (companyId) => `/admin/communications/newsletter-sending-accounts/${companyId}`,
      open: (companyId) => `/admin/communications/newsletter-sending-accounts/${companyId}`,
    },

    settings: {
      messaging: '/admin/settings/messaging',
    },
  },
};

// Intercepteur de réponse : deux issues seulement.
//
// Le serveur a déclaré la session finie : on la termine. Sinon l'erreur repart
// à l'appelant. L'intercepteur ne retente rien : chaque couche qui possède une
// requête porte déjà sa propre politique de reprise (SWR pour les données, le
// SessionProvider pour la session). Retenter ici en plus multipliait les appels
// par trois au moment précis où l'API est fragile, et trente tablettes sortant
// de veille ensemble aggravaient la panne au lieu de l'absorber.
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isSessionClosed(error)) {
        terminateSession();
      }

      return Promise.reject(error);
    },
);

export default axiosInstance;
